/**
 * Mg Theme — SubscriptionToggle
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Segmented pill toggle (One-time | Subscribe). Sets data-subscribe-mode
 * + data-auto-selling-plan-id on the .shopify-section root so CSS can
 * filter tiers by data-availability and product-form.js:buildCartItems
 * can attach selling_plan to eligible items.
 */
if (!customElements.get('mg-subscription-toggle')) {
  class MgSubscriptionToggle extends HTMLElement {
    connectedCallback() {
      this.sectionRoot = this.closest('.shopify-section');
      if (!this.sectionRoot) return;

      this.pillOneTime = this.querySelector('[data-ref="pill-onetime"]');
      this.pillSubscribe = this.querySelector('[data-ref="pill-subscribe"]');

      this.requireSubscribe = this.hasAttribute('data-require-subscribe');
      this.currentMode = this.requireSubscribe
        ? 'subscribe'
        : (this.dataset.defaultMode || 'one-time');
      /* Plan teaser only — the authoritative selected plan is owned by the
         frequency cards (<mg-subscription-plans>). Defaults to the first plan. */
      this.currentPlanId = parseInt(this.dataset.autoPlanId, 10);

      /* Per-variant selling-plan allocations + the live savings badge. Parsed
         BEFORE the first syncSection() so dataset.subscribeUnitPrice (consumed
         by mg-quantity-breaks + mg-sticky-atc) is correct from the start. */
      this.savingsBadge = this.querySelector('[data-ref="savings-badge"]');
      this.promoCode = (this.dataset.promoCode || '').trim();
      this.allocations = {};
      try {
        var json = this.querySelector('[data-selling-plans]');
        if (json) {
          var parsed = JSON.parse(json.textContent);
          this.allocations = parsed.allocations || {};
        }
      } catch (e) {
        if (window.Mg && Mg.debug) Mg.debug('SubToggle:parseAllocations', e);
      }

      /* Active variant id — used to resolve the plan teaser/badge per variant. */
      var idInput = this.sectionRoot.querySelector('form[data-product-form] input[name="id"]');
      this.currentVariantId = idInput ? parseInt(idInput.value, 10) : null;

      this.syncSection();
      this.syncPillState();

      this._onPillClick = this.onPillClick.bind(this);
      this._onKeyDown = this.onKeyDown.bind(this);
      if (this.pillOneTime) {
        this.pillOneTime.addEventListener('click', this._onPillClick);
        this.pillOneTime.addEventListener('keydown', this._onKeyDown);
      }
      if (this.pillSubscribe) {
        this.pillSubscribe.addEventListener('click', this._onPillClick);
        this.pillSubscribe.addEventListener('keydown', this._onKeyDown);
      }

      /* Re-validate the selected plan against the active variant's allocations.
         Allocations differ per variant (Shopify docs): a stale plan id would be
         POSTed to /cart/add.js and rejected. Synchronous data work — no fetch,
         no race. */
      this._onVariantChanged = this.onVariantChanged.bind(this);
      document.addEventListener('namy:variant:changed', this._onVariantChanged);

      /* Race-safe initial sync: notify consumers (qty-breaks overlay, sticky
         ATC price + CTA label) of the resolved initial mode + subscribe price.
         If a consumer CE upgraded after us it reads dataset directly; if before,
         it catches this event. */
      this.syncDrawerState();
      this.dispatchModeChange();
    }

    disconnectedCallback() {
      if (this.pillOneTime) {
        this.pillOneTime.removeEventListener('click', this._onPillClick);
        this.pillOneTime.removeEventListener('keydown', this._onKeyDown);
      }
      if (this.pillSubscribe) {
        this.pillSubscribe.removeEventListener('click', this._onPillClick);
        this.pillSubscribe.removeEventListener('keydown', this._onKeyDown);
      }
      if (this._onVariantChanged) {
        document.removeEventListener('namy:variant:changed', this._onVariantChanged);
      }
    }

    onPillClick(event) {
      var mode = event.currentTarget.dataset.mode;
      if (!mode || mode === this.currentMode) return;
      if (mode === 'one-time' && this.requireSubscribe) return;
      this.currentMode = mode;
      this.syncSection();
      this.syncPillState();
      this.syncDrawerState();
      this.dispatchModeChange();
    }

    /* Variant changed → re-resolve allocations for the new variant, ensure the
       selected plan is still valid (reset if not), force one-time when the
       variant has no subscription, refresh the savings badge, re-sync the input. */
    onVariantChanged(event) {
      var variantId = event && event.detail && event.detail.variantId;
      if (!variantId || !this.allocations) return;

      this.currentVariantId = variantId;
      var allocs = this.allocations[variantId] || [];
      var hasSub = allocs.length > 0;

      if (this.pillSubscribe) {
        this.pillSubscribe.disabled = !hasSub;
        this.pillSubscribe.setAttribute('aria-disabled', String(!hasSub));
      }

      if (!hasSub) {
        if (this.currentMode === 'subscribe' && !this.requireSubscribe) {
          this.currentMode = 'one-time';
          this.syncPillState();
          this.dispatchModeChange();
        }
        this._updateBadge(null);
        this.syncSection();
        return;
      }

      var found = false;
      for (var i = 0; i < allocs.length; i++) {
        if (allocs[i].planId === this.currentPlanId) { found = true; break; }
      }
      if (!found) {
        this.currentPlanId = allocs[0].planId;
      }

      this._updateBadge(allocs);
      this.syncSection();
      /* Variant changed while subscription available → re-notify consumers so
         qty-breaks + sticky ATC re-read the new variant's subscribe price. */
      this.dispatchModeChange();
    }

    /* Resolve the allocation for the active plan on a given variant's allocs
       (falls back to the first). Used by the savings-badge teaser + the
       toggle-only price fallback. */
    _resolveAlloc(allocs) {
      if (!allocs || !allocs.length) return null;
      for (var i = 0; i < allocs.length; i++) {
        if (allocs[i].planId === this.currentPlanId) return allocs[i];
      }
      return allocs[0];
    }

    /* Subscribed unit price (cents) for the active variant + plan, or 0 when the
       variant has no subscription. Used only in the toggle-only fallback (no
       subscription-plans cards present). */
    _activeSubUnitPrice() {
      var alloc = this._resolveAlloc(this.allocations[this.currentVariantId]);
      return alloc && alloc.price ? alloc.price : 0;
    }

    _updateBadge(allocs) {
      if (!this.savingsBadge) return;
      var tpl = this.savingsBadge.getAttribute('data-save-template') || '';
      var pct = 0;
      if (allocs && allocs.length) {
        var alloc = this._resolveAlloc(allocs);
        if (alloc && alloc.compareAtPrice > 0 && alloc.compareAtPrice > alloc.price) {
          pct = Math.round((alloc.compareAtPrice - alloc.price) / alloc.compareAtPrice * 100);
        }
      }
      if (pct > 0 && tpl) {
        this.savingsBadge.textContent = tpl.replace('__PCT__', pct);
        this.savingsBadge.hidden = false;
      } else {
        this.savingsBadge.hidden = true;
      }
    }

    /* DRY contract for Mg.cart.applyAllPendingCodes — promo applies only when
       the customer is actually in subscribe mode. */
    getPendingCodes() {
      if (this.currentMode !== 'subscribe') return [];
      return this.promoCode ? [this.promoCode] : [];
    }

    syncSection() {
      this.sectionRoot.dataset.subscribeMode = this.currentMode;

      /* Plan ownership: when a <mg-subscription-plans> block is present it owns
         the selected plan + price (frequency cards). When it is ABSENT (toggle-only
         setup), the toggle drives the first/default plan + its price so subscribe
         still works — backward-compatible, no regression. */
      if (!this.sectionRoot.querySelector('mg-subscription-plans')) {
        if (this.currentPlanId) {
          this.sectionRoot.dataset.autoSellingPlanId = String(this.currentPlanId);
        }
        this.sectionRoot.dataset.subscribeUnitPrice = String(this._activeSubUnitPrice());
      }

      /* Mirror into the hidden form input for non-JS submits (form fallback):
         carry the plan chosen (by cards or toggle) only while in subscribe mode. */
      var form = this.sectionRoot.querySelector('form[data-product-form]');
      if (!form) return;
      var input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }
      input.value = this.currentMode === 'subscribe'
        ? (this.sectionRoot.dataset.autoSellingPlanId || '')
        : '';
    }

    /* The two shapes speak different ARIA: the pill/cards selector is a tablist
       (aria-selected), the "rows" drawer is a radiogroup (aria-checked). Read
       the role off the element rather than the setting so the markup stays the
       single source of truth. */
    _checkedAttr(el) {
      return el.getAttribute('role') === 'radio' ? 'aria-checked' : 'aria-selected';
    }

    syncPillState() {
      if (this.pillOneTime) {
        var isOT = this.currentMode === 'one-time';
        this.pillOneTime.setAttribute(this._checkedAttr(this.pillOneTime), String(isOT));
        this.pillOneTime.classList.toggle('mg-sub-toggle__pill--active', isOT);
      }
      if (this.pillSubscribe) {
        var isSub = this.currentMode === 'subscribe';
        this.pillSubscribe.setAttribute(this._checkedAttr(this.pillSubscribe), String(isSub));
        this.pillSubscribe.classList.toggle('mg-sub-toggle__pill--active', isSub);
      }
    }

    /* A radiogroup is expected to move between its options with the arrows, and
       a shopper who reaches the selector by keyboard will try them. Tab still
       works; this only adds what the role promises. Ignored when the options
       are tabs, which already behave as two ordinary buttons. */
    onKeyDown(event) {
      var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
      if (keys.indexOf(event.key) === -1) return;
      var current = event.currentTarget;
      if (current.getAttribute('role') !== 'radio') return;
      var other = current === this.pillOneTime ? this.pillSubscribe : this.pillOneTime;
      if (!other || other.disabled) return;
      event.preventDefault();
      other.focus();
      other.click();
    }

    /* A drawer whose children are all hidden by the current mode keeps its own
       padding and border and shows as an empty band — measured at 33px after
       switching to subscribe, where the quantity tiers hide in favour of the
       plans block. Nothing in Liquid can know this: the hiding is done by CSS
       from the mode. So measure after layout and let one attribute drive both
       the drawer's visibility and which row keeps its bottom corners. */
    syncDrawerState() {
      var drawer = this.querySelector('[data-drawer]');
      if (!drawer) return;
      /* Measured synchronously, NOT in a requestAnimationFrame: rAF does not
         run in a tab that is not being painted, so a background tab would keep
         a stale drawer and show the empty band on return. Reading offsetHeight
         forces the style recalc we need right here, and the mode attribute the
         hiding depends on has already been written by syncSection(). */
      /* Un-hide FIRST. The attribute hides the drawer, so measuring while it is
         still set reports every child at zero and the state latches shut — it
         did, and coming back to one-time left the tiers hidden. */
      this.removeAttribute('data-drawer-empty');
      var kids = drawer.children;
      var visible = false;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].offsetHeight > 0 || kids[i].offsetWidth > 0) { visible = true; break; }
      }
      if (!visible) this.setAttribute('data-drawer-empty', '');
    }

    dispatchModeChange() {
      this.dispatchEvent(new CustomEvent('namy:subscribe-mode:changed', {
        bubbles: true,
        detail: { mode: this.currentMode, sellingPlanId: this.currentPlanId }
      }));
    }
  }

  customElements.define('mg-subscription-toggle', MgSubscriptionToggle);
}

/* ---------------------------------------------------------------------------
 * Shared ATC-label flip (singleton, page-lifetime).
 * SINGLE source of the "Add to cart" → "Add subscription to cart" flip for
 * EVERY CTA: any button[data-label-subscribe] with a [data-atc-label-text]
 * child opts in by markup alone (main ATC, sticky ATC, future). Driven by the
 * only emitter of namy:subscribe-mode:changed (the toggle above), so this
 * loads only on pages that have a subscription toggle.
 * ------------------------------------------------------------------------- */
if (!window.__namyAtcLabelFlip) {
  window.__namyAtcLabelFlip = true;
  document.addEventListener('namy:subscribe-mode:changed', function (event) {
    var subscribe = event && event.detail && event.detail.mode === 'subscribe';
    document.querySelectorAll('button[data-label-subscribe]').forEach(function (btn) {
      if (btn.disabled) return; /* never relabel a sold-out/disabled CTA */
      var labelEl = btn.querySelector('[data-atc-label-text]');
      if (!labelEl) return;
      if (subscribe) {
        if (labelEl.dataset.labelOriginal == null) {
          labelEl.dataset.labelOriginal = labelEl.textContent.trim();
        }
        labelEl.textContent = btn.getAttribute('data-label-subscribe');
      } else if (labelEl.dataset.labelOriginal != null) {
        labelEl.textContent = labelEl.dataset.labelOriginal;
      }
    });
  });
}
